import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled, alpha } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import { useTour, PLATFORM_STEPS } from '../tour/tourState';
import { navPulse } from '../discovery/GoalTourGuide';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import {
  GoalsIcon,
  DashboardIcon,
  DataSourcesIcon,
  DataSignalsIcon,
  DiscoveryIcon,
  RelationshipsIcon,
  SegmentsIcon,
  ModelsIcon,
  PipelinesIcon,
  TrainingsIcon,
  DeploymentsIcon,
  InferencesIcon,
} from './CustomIcons';
import { useSidebar } from './SidebarContext';
import { fetchRequest as goalsFetch } from '../../redux/actions/discovery/goals';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';

// Custom icon family (see CustomIcons.js) — one metaphor per surface instead
// of grab-bag Material glyphs.
const iconMap = {
  dashboard: DashboardIcon,
  datasources: DataSourcesIcon,
  signals: DataSignalsIcon,
  blueprints: ModelsIcon,
  trainings: TrainingsIcon,
  segments: SegmentsIcon,
  pipelines: PipelinesIcon,
  deployments: DeploymentsIcon,
  forecasts: InferencesIcon,
  discovery: DiscoveryIcon,
  relationships: RelationshipsIcon,
  goals: GoalsIcon,
};

// Icon per goal type, shown on each goal pill in the sidebar.
const goalTypeIconMap = {
  forecast_value: ShowChartIcon,
  forecast_distribution: BarChartIcon,
  trend: TrendingUpIcon,
  anomaly: WarningAmberIcon,
};

const topNavItems = [
  { icon: 'dashboard', text: 'Dashboard', path: '/' },
];

// Pop-in for the page-link bubble that attaches to a rail item.
const sidebarBubbleIn = keyframes`
  from { opacity: 0; transform: translateX(-6px) scale(0.92); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

// Workspace-scoped surfaces, grouped under one "Workspace" heading in the
// rendered menu. Sub-group titles stay as smaller labels inside it.
const navSections = [
  {
    // Raw inputs + the Signal Universe (signals/events/states/entities).
    title: 'Data',
    items: [
      { icon: 'datasources', text: 'Data Sources', path: '/datasources' },
      { icon: 'signals', text: 'Data Signals', path: '/signals' },
    ],
  },
  {
    // The world model / understanding layer. Placeholder routes for now;
    // backend wired in later phases (see /plans).
    title: 'Intelligence',
    // Short form for the collapsed rail, where the full word won't fit.
    collapsedTitle: 'Intel',
    items: [
      // Sidebar labels kept short so they never wrap at 180px. Goal-driven
      // driver discovery lives under Actions → Goals; Discovery here is the
      // open-ended AI insights feed over all workspace signals.
      { icon: 'discovery', text: 'Discovery', path: '/discovery' },
      { icon: 'relationships', text: 'Relationships', path: '/relationships' },
      // The conversational workbench (/analyst) intentionally has NO
      // sidebar entry: it lives in the bottom-right dock/pill instead.
    ],
  },
  {
    // The engine room: features + models that power the Actions above.
    title: 'Modeling',
    items: [
      // A Segment is the training-ready dataset / feature grid. Lives here
      // (not under Data) because it is a modeling input; Goals auto-provision
      // one, and power users edit it here.
      { icon: 'segments', text: 'Segments', path: '/segments' },
      // "Models" = blueprints (custom / foundation / external / BYOM). Entry
      // point for everything model-shaped; path stays /blueprints.
      { icon: 'blueprints', text: 'Models', path: '/models' },
      // "Pipelines" = the workspace-scoped, scheduled trainer (Model + Segment
      // -> Trainings). /pipelines is the canonical URL; /models* is an alias.
      { icon: 'pipelines', text: 'Pipelines', path: '/pipelines' },
      // Workspace-wide list of every training run produced by any pipeline.
      { icon: 'trainings', text: 'Trainings', path: '/trainings' },
    ],
  },
  {
    title: 'Serving',
    items: [
      { icon: 'deployments', text: 'Deployments', path: '/deployments' },
      { icon: 'forecasts', text: 'Inferences', path: '/inference' },
    ],
  },
];

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  // Keep in sync with --sidebar-width / body.sidebar-collapsed in themed.css.
  width: collapsed ? 64 : 173,
  height: '100vh',
  backgroundColor: theme.palette.background.sidebar,
  boxShadow: theme.palette.boxShadow,
  position: 'fixed',
  zIndex: 101,
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'visible',
  transition: 'width 200ms ease',
}));

const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${theme.spacing(1.25)} 0`,
  height: 52,
}));

// Rendered as a sibling of the rail (not a child) so its stacking is
// independent: above the rail (z 101) and above the goals flyout (z 1300),
// so the full circle always shows.
const CollapseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  position: 'fixed',
  // In the band between the logo (52px tall) and the Goals strip below it;
  // the menu's top padding keeps that band clear.
  top: 64,
  // Straddles the rail's right edge; follows the rail's width animation.
  left: (collapsed ? 64 : 173) - 11,
  transition: 'left 200ms ease',
  width: 22,
  height: 22,
  padding: 0,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.sidebar,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.boxShadow,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  '& svg': {
    fontSize: 16,
  },
  zIndex: 1301,
}));

const LogoImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ collapsed }) => ({
  paddingRight: collapsed ? 0 : 7,
  width: 22,
  display: 'inline',
  verticalAlign: 'bottom',
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 400,
  color: theme.palette.text.secondary,
  textAlign: 'left',
  marginTop: -1
}));

const SloganText = styled(Typography)(({ theme }) => ({
  marginTop: -1,
  marginLeft: 2,
  fontFamily: theme.typography.fontFamily.secondary,
  color: theme.palette.text.primary,
  fontSize: 14,
  fontWeight: 500
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: `${theme.spacing(1.5)} ${theme.spacing(2.25)} ${theme.spacing(0.5)}`,
}));

// Sub-group titles inside the Workspace section — visually subordinate to
// SectionLabel: smaller, dimmer, tighter.
const SubSectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: alpha(theme.palette.text.secondary, 0.72),
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.25)} ${theme.spacing(0.25)}`,
}));

// Collapsed-rail group titles — one tiny centered word above each icon group,
// sized to fit the longest title ("Intelligence") in the 64px rail.
const CollapsedSectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 7.5,
  fontWeight: 700,
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  color: alpha(theme.palette.text.secondary, 0.85),
  padding: `${theme.spacing(1.25)} 0 ${theme.spacing(0.5)}`,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '60%',
    width: '4px',
    backgroundColor: 'transparent',
    transition: 'background-color 120ms ease',
    zIndex: 1
  },
  '&:hover::after': {
    backgroundColor: alpha(theme.palette.primary.main, 0.35)
  },
  '&.active-item::after': {
    backgroundColor: theme.palette.primary.main
  }
}));

const StyledNavLink = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  color: theme.palette.text.primary,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  // Collapsed: icon-only rows — centered, roomier, no expanded-mode offsets.
  justifyContent: collapsed ? 'center' : 'flex-start',
  textDecoration: 'none',
  padding: collapsed
    ? `${theme.spacing(0.875)} 0`
    : `${theme.spacing(0.25)} ${theme.spacing(1)}`,
  fontFamily: theme.typography.fontFamily,
  '&.active-style': {
    backgroundColor: theme.palette.primary.light,
    color: `${theme.palette.primary.main} !important`,
    '& *': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiListItemText-root': {
      color: `${theme.palette.primary.main} !important`,
      '& .MuiTypography-root': {
        color: `${theme.palette.primary.main} !important`,
        fontWeight: 600
      },
      '& span': {
        color: `${theme.palette.primary.main} !important`,
        fontWeight: 600
      }
    },
    '& .MuiTypography-root': {
      fontWeight: 600,
      color: `${theme.palette.primary.main} !important`
    },
    '&.menu-bar-item': {
      color: `${theme.palette.primary.main} !important`,
      '& .MuiTypography-root': {
        color: `${theme.palette.primary.main} !important`,
      }
    }
  },
  '&.menu-bar-item': {
    fontFamily: theme.typography.fontFamily,
  },
  '& svg': collapsed
    ? { maxHeight: 'none', fontSize: 19, marginLeft: 0 }
    : { maxHeight: 13, fontSize: 17, marginLeft: 10 },
  '& .MuiListItemIcon-root': {
    minWidth: collapsed ? 0 : 30,
    justifyContent: 'center',
    fontSize: 10,
    // Softened a notch from the label color so icons support the text
    // instead of competing with it.
    color: alpha(theme.palette.text.primary, 0.62),
  },
  '& .MuiListItemText-root': {
    marginLeft: 8,
    marginTop: 2,
    marginBottom: 2
  },
  '& .MuiTypography-root': {
    fontSize: 13.5,
    fontWeight: 400,
    letterSpacing: '0.005em',
    lineHeight: 1.3
  }
}));

// Goals root item — the flagship entry at the very top of the rail. Bigger
// and heavier than regular nav rows so the goal-centric model reads at a
// glance; the lime icon keeps it branded even when inactive.
const GoalsRootLink = styled(NavLink)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  textDecoration: 'none',
  margin: '8px',
  padding: '9px 10px',
  borderRadius: 8,
  color: theme.palette.text.primary,
  transition: 'background-color 120ms ease, color 120ms ease',
  '& svg': {
    flexShrink: 0,
    color: theme.palette.primary.main,
    transition: 'color 120ms ease',
  },
  // Active indicator: a lime bar hugging the sidebar's left edge (the row
  // has an 8px margin, hence the negative offset) instead of a filled pill.
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -8,
    top: '18%',
    bottom: '18%',
    width: 3,
    borderRadius: '0 2px 2px 0',
    backgroundColor: 'transparent',
    transition: 'background-color 120ms ease',
  },
  // Hover and open-flyout share the panel's surface color, so row + panel
  // light up as one connected layer while the flyout is showing.
  '&:hover, &.flyout-open': {
    backgroundColor: theme.palette.background.sidebarHover,
  },
  // NavLink's automatic `active` class (a function className would be
  // swallowed by the styled() wrapper).
  '&.active': {
    '&::before': { backgroundColor: theme.palette.primary.main },
    '& .MuiTypography-root': { color: theme.palette.primary.main },
  },
}));

// Goal row — flat like the rest of the nav (no filled pill). A slim accent
// bar on the left carries the hover/active state, so the list reads as a
// quiet index of destinations rather than a stack of buttons.
const GoalButton = styled(NavLink)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  // Top-aligned so the icon sits beside the first line when a name wraps.
  alignItems: 'flex-start',
  gap: 9,
  textDecoration: 'none',
  margin: '1px 8px',
  padding: '6px 8px 6px 10px',
  borderRadius: 6,
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  transition: 'background-color 120ms ease, color 120ms ease',
  // Active indicator: a small lime bar on the row's right edge — no fill.
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '25%',
    bottom: '25%',
    width: 2.5,
    borderRadius: 2,
    backgroundColor: 'transparent',
    transition: 'background-color 120ms ease',
  },
  '& svg': {
    fontSize: 15,
    flexShrink: 0,
    // Optically centers the icon against the first text line (13px/1.35).
    marginTop: 1.5,
    color: theme.palette.text.secondary,
    transition: 'color 120ms ease',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // NavLink adds the default `active` class on match; a function className
  // would be swallowed by the styled() wrapper, so target that class.
  '&.active': {
    color: theme.palette.primary.main,
    '&::after': { backgroundColor: theme.palette.primary.main },
    '& svg': { color: theme.palette.primary.main },
    '& .MuiTypography-root': { color: theme.palette.primary.main, fontWeight: 600 },
  },
}));

// "Create a Goal" as a ghost row — same geometry as GoalButton (so the + lines
// up under the goal-type icons and the label under the goal names) but no fill.
const GoalCreateLink = styled(NavLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  textDecoration: 'none',
  margin: '1px 8px',
  padding: '5px 8px 5px 10px',
  borderRadius: 6,
  color: theme.palette.primary.main,
  transition: 'background-color 120ms ease',
  '& svg': { fontSize: 15, flexShrink: 0, color: theme.palette.primary.main },
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.10) },
}));

const Sidebar = () => {
  const location = useLocation();
  const theme = useTheme();
  const { collapsed, isMobile, toggle } = useSidebar();
  // Touch browsers synthesize mouseenter/mouseover at the finger's
  // position after taps and scroll gestures, which ghost-triggers the
  // rail's hover UI (tooltips, the Goals flyout) while the user is just
  // scrolling the page. Hover affordances are for devices that hover.
  const canHover = useMediaQuery('(hover: hover)');

  const dispatch = useDispatch();
  const goalsResponse = useSelector((state) => state.discovery_goals?.response);
  const goals = Array.isArray(goalsResponse) ? goalsResponse : [];
  React.useEffect(() => {
    dispatch(goalsFetch());
  }, [dispatch]);

  // Keep the sidebar goal list live: a goal created/deleted elsewhere, or any
  // goal's status change, refreshes the menu without a page reload.
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string' && (
      msg.event === 'goal.created'
      || msg.event === 'goal.deleted'
      || msg.event.indexOf('goal.status') === 0
      || msg.event.indexOf('goal.run.') === 0
    ),
    () => dispatch(goalsFetch()),
  );

  // Goals flyout: opens on hover over the Goals nav item, stays open while
  // the pointer is inside either the item or the panel. The close is delayed
  // a beat so the pointer can cross the gap between them.
  const [goalsFlyoutOpen, setGoalsFlyoutOpen] = React.useState(false);
  const goalsItemRef = React.useRef(null);
  const goalsCloseTimer = React.useRef(null);
  const openGoalsFlyout = React.useCallback(() => {
    clearTimeout(goalsCloseTimer.current);
    setGoalsFlyoutOpen(true);
  }, []);
  const scheduleGoalsFlyoutClose = React.useCallback(() => {
    clearTimeout(goalsCloseTimer.current);
    goalsCloseTimer.current = setTimeout(() => setGoalsFlyoutOpen(false), 150);
  }, []);
  const closeGoalsFlyout = React.useCallback(() => {
    clearTimeout(goalsCloseTimer.current);
    setGoalsFlyoutOpen(false);
  }, []);
  React.useEffect(() => () => clearTimeout(goalsCloseTimer.current), []);

  const goalsActive = location.pathname === '/goals'
    || location.pathname.startsWith('/goals/');

  // Platform tour: pulse the sidebar item of the page the tour is on, so
  // users anchor page <-> nav while being walked around. null outside the
  // tour (the overwhelming majority of renders).
  const tour = useTour();
  const tourPath = tour?.phase === 'platform'
    ? (PLATFORM_STEPS[tour.idx]?.path || null) : null;

  // Chat page links (show_page): instead of navigating anywhere, a
  // transient bubble attaches to the matching sidebar item - "this page
  // lives HERE" - and the item pulses. Click the bubble to actually go.
  const navigateTo = useNavigate();
  const lastPageTour = useSelector((s) => s.signal_ask_data?.lastPageTour || null);
  const [pageHint, setPageHint] = React.useState(null);
  const [pageHintAnchor, setPageHintAnchor] = React.useState(null);
  const handledTourTs = React.useRef(0);
  const pageHintTimer = React.useRef(null);
  React.useEffect(() => {
    if (!lastPageTour || lastPageTour.ts === handledTourTs.current) return;
    handledTourTs.current = lastPageTour.ts;
    const spec = lastPageTour.spec || {};
    if (!spec.path) return;
    setPageHint({ path: spec.path, title: spec.title || 'Open page', note: spec.note || '' });
    clearTimeout(pageHintTimer.current);
    pageHintTimer.current = setTimeout(() => setPageHint(null), 12000);
  }, [lastPageTour]);
  React.useEffect(() => () => clearTimeout(pageHintTimer.current), []);

  const pageHintMatches = (itemPath) => Boolean(
    pageHint && itemPath && (
      pageHint.path === itemPath
      || (itemPath !== '/' && pageHint.path.startsWith(`${itemPath}/`))
    ),
  );
  const setPageHintAnchorCb = React.useCallback((el) => setPageHintAnchor(el), []);

  const renderNavItem = (item) => {
    // ``exact`` matches one path only; ``prefix`` keeps the item active across
    // its nested routes (e.g. a goal stays lit on /goals/:id/drivers) while
    // never matching a sibling whose id merely shares a string prefix; the
    // default falls back to a loose startsWith match.
    const isActive = item.exact
      ? location.pathname === item.path
      : item.prefix
        ? (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
        : (location.pathname === item.path
          || (item.path !== '/' && location.pathname.startsWith(item.path)));
    const primaryColor = theme.palette.primary.main;
    const hasBadge = item.badgeNumber != null;
    const IconComponent = (item.noIcon || hasBadge) ? null : (item.iconComponent || iconMap[item.icon]);
    const link = (
      <StyledNavLink
        to={item.path}
        collapsed={collapsed}
        className={() => (isActive ? 'active-style' : '')}
        style={isActive ? { color: primaryColor } : {}}
      >
        {(IconComponent || hasBadge) && (
          <ListItemIcon style={isActive ? { color: primaryColor } : {}}>
            {hasBadge ? (
              <Box
                sx={{
                  ml: '10px',
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {item.badgeNumber}
              </Box>
            ) : (
              React.createElement(IconComponent, item.iconSx ? { sx: item.iconSx } : {})
            )}
          </ListItemIcon>
        )}
        {!collapsed && (
          <ListItemText
            primary={item.text}
            sx={{ minWidth: 0, my: 0 }}
            style={isActive ? { color: primaryColor } : {}}
            primaryTypographyProps={{
              noWrap: true,
              title: item.text,
              style: isActive ? {
                color: primaryColor,
                fontWeight: 600
              } : {}
            }}
          />
        )}
      </StyledNavLink>
    );
    const hinted = pageHintMatches(item.path);
    const toured = (tourPath != null && tourPath === item.path) || hinted;
    return (
      <StyledListItem
        key={item.key || item.text}
        disablePadding
        className={isActive ? 'active-item' : ''}
        ref={hinted ? setPageHintAnchorCb : undefined}
        sx={toured ? {
          position: 'relative',
          borderRadius: '8px',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 1,
            borderRadius: '8px',
            color: 'primary.main',
            animation: `${navPulse} 1.8s ease-out infinite`,
            pointerEvents: 'none',
            zIndex: 1,
          },
        } : undefined}
      >
        {collapsed && canHover ? (
          <Tooltip title={item.text} placement="right" arrow>
            {link}
          </Tooltip>
        ) : link}
      </StyledListItem>
    );
  };

  return (
    <>
      {/* On mobile the rail is pinned to the icon column - no toggle. */}
      {!isMobile && (
      <Tooltip
        title={canHover ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : ''}
        placement="right"
        arrow
      >
        <CollapseButton
          onClick={toggle}
          size="small"
          collapsed={collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </CollapseButton>
      </Tooltip>
      )}

    <SidebarContainer collapsed={collapsed}>
      <LogoContainer collapsed={collapsed}>
        <Box
          component={NavLink}
          to="/"
          aria-label="Go to dashboard"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <Box display="inline-block" sx={{ verticalAlign: 'middle', lineHeight: 0 }}>
            <LogoImage src={theme.logo} alt="CelMind Predict.ai" collapsed={collapsed} />
          </Box>
          {!collapsed && (
            <Box display="inline-block" sx={{ verticalAlign: 'middle' }}>
              <SloganText>Predict.ai</SloganText>
            </Box>
          )}
        </Box>
      </LogoContainer>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Clear the collapse arrow sitting between the logo and the Goals
          // strip.
          pt: '28px',
          // Clear the absolutely-positioned bottom block so the last menu
          // items never hide behind it on short viewports.
          pb: 12,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'divider',
            borderRadius: 3,
          },
        }}
      >
        {/* Goals — the flagship item at the very top of the rail. The goal
            list itself lives in a hover flyout so the rail keeps a fixed
            height no matter how many goals exist, and full goal names get a
            panel wide enough to breathe. */}
        <Box
          onMouseEnter={canHover ? openGoalsFlyout : undefined}
          // The flyout Popper is a React child of this strip, so moving
          // pointer panel → strip never re-fires onMouseEnter (React-tree
          // enter/leave treats the portal as "inside"); the panel's leave
          // would then close the flyout under the pointer. onMouseOver fires
          // on every movement within the strip and cancels that close.
          // Touch devices skip all of it - tapping Goals just navigates.
          onMouseOver={canHover ? openGoalsFlyout : undefined}
          onMouseLeave={canHover ? scheduleGoalsFlyoutClose : undefined}
        >
          {collapsed && (
            <CollapsedSectionLabel sx={{ pt: 1, pb: 0 }}>
              Goals
            </CollapsedSectionLabel>
          )}
          {/* No 'active-item' class and no hover edge-bar here: the Goals row
              carries its own filled active style, and the flyout connects to
              the rail edge — an indicator bar would draw a seam between them. */}
          {/* While the flyout is showing, only this row (icon + its margins,
              edge to edge — NOT the GOALS label above it) takes the panel's
              surface. The panel is top-aligned to this same row, so row and
              panel form one continuous shape with no extra edges. */}
          <StyledListItem
            disablePadding
            ref={goalsItemRef}
            sx={{
              '&::after': { display: 'none' },
              backgroundColor: goalsFlyoutOpen ? 'background.sidebarHover' : 'transparent',
              transition: 'background-color 120ms ease',
            }}
          >
            <GoalsRootLink
              to="/goals"
              className={goalsFlyoutOpen ? 'flyout-open' : ''}
              // Collapsed: fill the row minus margins (width:100% would
              // overflow the 60px rail and knock the icon off-center).
              sx={collapsed ? { justifyContent: 'center', px: 0, flex: 1, width: 'auto' } : {}}
            >
              <GoalsIcon sx={{ fontSize: 20 }} />
              {!collapsed && (
                <>
                  <Typography
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 15,
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      lineHeight: 1.3,
                    }}
                  >
                    Goals
                  </Typography>
                  {goals.length > 0 && (
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.1,
                        borderRadius: 99,
                        border: '1px solid',
                        borderColor: goalsActive ? 'primary.main' : 'divider',
                        color: goalsActive ? 'primary.main' : 'text.secondary',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {goals.length}
                    </Box>
                  )}
                  <ChevronRightIcon
                    sx={{
                      fontSize: '15px !important',
                      color: goalsActive ? 'primary.main' : 'text.secondary',
                      opacity: 0.7,
                    }}
                  />
                </>
              )}
            </GoalsRootLink>
          </StyledListItem>

          <Popper
            open={goalsFlyoutOpen}
            anchorEl={goalsItemRef.current}
            placement="right-start"
            sx={{ zIndex: 1300 }}
            // Flush with the rail edge (tucked 2px under it so the rail's
            // own shadow never shows between them) and level with the Goals
            // row — the panel reads as the sidebar growing a second column.
            modifiers={[{ name: 'offset', options: { offset: [0, -2] } }]}
          >
            <Paper
              elevation={0}
              // The Popper portals to <body>, so the panel needs its own
              // hover handlers to hold the flyout open.
              onMouseEnter={openGoalsFlyout}
              onMouseLeave={scheduleGoalsFlyoutClose}
              sx={(t) => ({
                width: 272,
                maxHeight: 440,
                overflowY: 'auto',
                // One step lighter than the rail — matches the hovered Goals
                // row exactly, so row + panel read as one connected layer.
                // Same edge treatment as the rail (no border, same shadow).
                backgroundColor: 'background.sidebarHover',
                backgroundImage: 'none',
                borderRadius: '0 8px 8px 0',
                border: 'none',
                boxShadow: t.palette.boxShadow,
                // Clip the shadow at the panel's left AND top edges: left so
                // it never draws a seam over the Goals strip it connects to,
                // top so it never darkens the collapse arrow sitting just
                // above the panel.
                clipPath: 'inset(0 -16px -16px 0)',
                py: 0.75,
              })}
            >
              {goals.length > 0 ? (
                goals.map((g) => {
                  const GoalIcon = goalTypeIconMap[g.goal_type] || GoalsIcon;
                  const name = g.name || 'Untitled goal';
                  return (
                    <GoalButton
                      key={g.goal_id}
                      to={`/goals/${g.goal_id}`}
                      onClick={closeGoalsFlyout}
                    >
                      <GoalIcon />
                      <Typography
                        noWrap
                        title={name}
                        sx={{ fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
                      >
                        {name}
                      </Typography>
                    </GoalButton>
                  );
                })
              ) : (
                <Typography
                  sx={{ px: 2, py: 1, fontSize: 13, color: 'text.secondary' }}
                >
                  No goals yet — create your first one.
                </Typography>
              )}
              <Divider sx={{ my: 0.75, mx: 1.5 }} />
              <GoalCreateLink to="/goals/create" onClick={closeGoalsFlyout}>
                <AddIcon />
                <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.2 }}>
                  Create a Goal
                </Typography>
              </GoalCreateLink>
              <GoalCreateLink
                to="/goals"
                onClick={closeGoalsFlyout}
                sx={{
                  color: 'text.secondary',
                  '& svg': { color: 'text.secondary' },
                }}
              >
                <GoalsIcon sx={{ fontSize: 15 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 400, lineHeight: 1.2 }}>
                  View all goals
                </Typography>
              </GoalCreateLink>
            </Paper>
          </Popper>
        </Box>

        {/* Workspace — everything scoped to the workspace rather than to a
            single goal. Sub-groups keep their labels, subordinated under the
            one Workspace heading. */}
        <Divider
          sx={{
            mt: 1.25,
            mb: 0.25,
            mx: collapsed ? 1.5 : 2.25,
            borderColor: 'divider',
            borderBottomWidth: 2,
          }}
        />
        {!collapsed && <SectionLabel>Workspace</SectionLabel>}
        {/* Dashboard leads the workspace scope: it aggregates everything
            below it, so it sits above the sub-groups without one of its own. */}
        <List disablePadding>
          {topNavItems.map(renderNavItem)}
        </List>
        {navSections.map((section) => (
          <React.Fragment key={section.title}>
            {collapsed ? (
              <CollapsedSectionLabel>
                {section.collapsedTitle || section.title}
              </CollapsedSectionLabel>
            ) : (
              <SubSectionLabel>{section.title}</SubSectionLabel>
            )}
            <List disablePadding>
              {section.items.map(renderNavItem)}
            </List>
          </React.Fragment>
        ))}
      </Box>

      {/* Settings — pinned to the rail's bottom edge, outside the scroll
          area so it's always reachable. Icon-only when collapsed (with a
          tooltip), icon + "Settings" label when expanded. */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 2.5, flexShrink: 0 }}>
        <List disablePadding>
          {renderNavItem({
            iconComponent: SettingsIcon,
            text: 'Settings',
            path: '/settings',
            prefix: true,
            key: 'settings',
          })}
        </List>
      </Box>

      {/* Chat page-link bubble: "this page lives HERE". Anchored to the
          matching rail item (or the Goals item for goal pages); clicking
          it opens the page, otherwise it fades on its own. */}
      <Popper
        open={Boolean(pageHint) && Boolean(
          pageHint?.path?.startsWith('/goals') ? goalsItemRef.current : pageHintAnchor,
        )}
        anchorEl={pageHint?.path?.startsWith('/goals') ? goalsItemRef.current : pageHintAnchor}
        placement="right"
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
      >
        <Box
          onClick={() => {
            const p = pageHint?.path;
            setPageHint(null);
            if (p) navigateTo(p);
          }}
          sx={(t) => {
            const dark = t.palette.mode === 'dark';
            const fill = dark ? (t.brand?.lime || '#c0fb6e') : (t.brand?.teal || t.palette.primary.main);
            return {
              position: 'relative',
              maxWidth: 240,
              px: 1.5, py: 1,
              borderRadius: '12px',
              bgcolor: fill,
              color: dark ? '#101708' : '#ffffff',
              cursor: 'pointer',
              boxShadow: `0 10px 30px ${alpha(fill, 0.35)}, 0 4px 14px rgba(0,0,0,0.3)`,
              animation: `${sidebarBubbleIn} 240ms cubic-bezier(0.34, 1.3, 0.44, 1) both`,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -4, top: '50%',
                width: 10, height: 10,
                bgcolor: fill,
                transform: 'translateY(-50%) rotate(45deg)',
              },
            };
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.35 }}>
            {pageHint?.title}
          </Typography>
          {pageHint?.note ? (
            <Typography sx={{ fontSize: 11.5, lineHeight: 1.45, opacity: 0.85, mt: 0.25 }}>
              {pageHint.note}
            </Typography>
          ) : null}
        </Box>
      </Popper>

    </SidebarContainer>
    </>
  );
};

export default Sidebar;
